import { useTheme } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { Suspense, useEffect } from 'react'
import { Outlet } from 'react-router-dom'

import LoadingPaperSkeleton from '~/components/loadingPaperSkeleton'
import LoadingWithBackdrop from '~/components/loadingWithBackdrop'
import { TRANSPARENT_IMAGE } from '~/constants'
import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import useAuthListener from '~/features/auth/hooks/useAuthListener'
import useAxiosInterceptors from '~/features/auth/hooks/useAxiosInterceptors'
import { queryKeys } from '~/features/auth/utils'
import useIsPartner from '~/features/partners/hooks/useIsPartner'
import usePartnerCentralized from '~/features/partners/hooks/usePartnerCentralized'
import { isLibbyDomain } from '~/features/partners/utils'
import useHydrateStore from '~/hooks/useHydrateStore'
import useScrollToTop from '~/hooks/useScrollToTop'
import { addCookieTrackerScriptIfNeeded } from '~/utils/misc'

function RootConfigLayout() {
  const theme = useTheme()
  const isPartner = useIsPartner() || !isLibbyDomain()

  const queryClient = useQueryClient()

  useGetAgentCentralized()

  useEffect(() => {
    queryClient.resetQueries()
    queryClient.prefetchQuery({ queryKey: queryKeys.authSession() })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useScrollToTop()
  useAuthListener()
  useHydrateStore()
  useAxiosInterceptors()

  const { isLoading: isLoadingPartnerData, partner } = usePartnerCentralized()

  useEffect(() => {
    addCookieTrackerScriptIfNeeded()
  }, [])

  useEffect(() => {
    const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement
    if (link && isPartner) {
      link.href = TRANSPARENT_IMAGE // this will be partner.favicon but we don't have it yet in the db
    }
  }, [isPartner])

  /* 
  The loader uses hard-coded colors, because it is 
  displayed before colors are available and before
  we know it's a partner / libby theme.
  We cannot expose libby theme for partner site, nor
  the other way around.
  */

  if (isLoadingPartnerData && !partner.id) {
    return (
      <LoadingWithBackdrop
        show
        sx={{
          backgroundColor: theme.palette.common.zircon,
          color: theme.palette.common.midnight,
          zIndex: 9999,
        }}
      />
    )
  }

  return (
    <Suspense fallback={<LoadingPaperSkeleton />}>
      <Outlet />
    </Suspense>
  )
}

export default RootConfigLayout
